import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import ClientService from "../../Services/clientService";
import styles from "./Clients.module.css";
import i18n from "../../Services/i18n";
import { useToasts } from "../../LayoutComponents/ToastContext";
import { useModal } from "../../Context/ModalContext";
import { usePrivileges } from "../../Context/PrivilegeContext";

import ClientInfoTab from "../../Components/ClientTabs/ClientInfoTab";
import ClientHorsesTab from "../../Components/ClientTabs/ClientHorsesTab";
import ClientNotesTab from "../../Components/ClientTabs/ClientNotesTab";
import ClientMediasTab from "../../Components/ClientTabs/ClientMediasTab";
import ClientServicesTab from "../../Components/ClientTabs/ClientServicesTab";
import { useClients } from "../../Context/ClientsContext";
import ClientQuickForm from "../../Components/QuickFormComponents/ClientQuickForm/ClientQuickForm";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import ListCardContainer from "../../Components/ListCard/ListCardContainer";
import PrivilegeValidationService from "../../Services/privilegeValidationService";
import ErrorBoundary from "../../Components/ErrorBoundary";
import CrashTest from "../../Components/CrashTest";

const Tabs = {
  INFO: "Infos",
  HORSES: "Chevaux",
  SERVICES: "Services reçus",
  MEDIA: "Médias",
  NOTES: "Notes",
};

const Clients = () => {
  const location = useLocation();
  const { paramClientId: initialClientId } = location.state || {};
  const [paramClientId, setParamClientId] = useState(initialClientId);
  const [loadedClientId, setLoadedClientId] = useState();
  const [selectedClient, setSelectedClient] = useState();
  const [selectedClientDetail, setSelectedClientDetail] = useState();
  const [filterText] = useState("");
  const [sortDirection] = useState("asc");
  const [sortField] = useState("name");
  const [activeTab, setActiveTab] = useState(Tabs.INFO);
  const [isLoadingClientDetail, setIsLoadingClientDetail] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const { addToast } = useToasts();
  const { openModal, closeModal } = useModal();
  const { clientsData, fetchClients, isFetchingClients } = useClients();
  const { privileges } = usePrivileges();

  const clientQuickFormRef = useRef(null);
  const clientListRef = useRef({});
  const isArchived = selectedClientDetail?.isArchived === true;
  const [formKeys, setFormKeys] = useState({
    formClient: 0,
  });

  useEffect(() => {
    if (paramClientId && clientsData.length > 0) {
      const client = clientsData.find((c) => c.externalId === paramClientId);
      if (client) {
        handleSelectClient(client);
      }
      setParamClientId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramClientId, clientsData]);

  useEffect(() => {
    if (loadedClientId) {
      const client = clientsData.find((x) => x.externalId === loadedClientId);
      if (client) {
        handleSelectClient(client);
      } else {
        addToast(i18n.t("Generic.NotFoundError"), "CLIENTNOTFOUND", "ERROR", true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedClientId, clientsData]);

  const handleSelectClient = async (client, refreshClient = false) => {
    try {
      if (!refreshClient && selectedClient && client.externalId === selectedClient.externalId) {
        return;
      }
      setIsLoadingClientDetail(true);
      const clientDetail = await ClientService.GetDetailClient(client.externalId);
      setSelectedClientDetail(clientDetail);
      setSelectedClient(client);

      if (!refreshClient) {
        const criticalNotes = clientDetail?.notes?.filter((e) => e.isCritical) ?? [];
        if (criticalNotes.length) {
          criticalNotes.forEach((e) => {
            addToast(e.note, e.externalId, "CUSTOM", true, e.color);
          });
        }
      }

      clientListRef.current.scrollToItem(client.externalId);
      setIsLoadingClientDetail(false);
    } catch (ex) {
      console.log(ex);
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const refreshClientList = async (clientExternalId) => {
    try {
      await fetchClients();

      if (clientExternalId) {
        const clientToRefresh =
          selectedClient?.externalId === clientExternalId
            ? selectedClient
            : { externalId: clientExternalId };

        await handleSelectClient(clientToRefresh, true);
      } else {
        setSelectedClient(undefined);
        setSelectedClientDetail(undefined);
      }
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const handleCloseForm = (formName) => {
    setFormKeys((prevKeys) => ({
      ...prevKeys,
      [formName]: prevKeys[formName] + 1,
    }));
  };

  const getFilteredAndSortedClients = () => {
    if (!clientsData) return [];
    return clientsData
      .filter(
        (client) =>
          !filterText.trim() ||
          client.firstName?.toLowerCase().includes(filterText.toLowerCase()) ||
          client.lastName?.toLowerCase().includes(filterText.toLowerCase()),
      )
      .sort((a, b) => {
        let result;
        if (sortField === "firstName") {
          result = a.firstName?.localeCompare(b.firstName || "");
        } else if (sortField === "lastName") {
          result = a.lastName?.localeCompare(b.lastName || "");
        } else {
          result = new Date(a.createdOn) - new Date(b.createdOn);
        }
        return sortDirection === "asc" ? result : -result;
      });
  };

  const handleAddClientClick = async () => {
    if (!PrivilegeValidationService.CanAddClient(await privileges(), clientsData.length)) {
      addToast(
        i18n.t("Privilege.ValidationError.MaxClients"),
        "PrivilegeValidationFailed",
        "ERROR",
        true,
      );
      return;
    }
    openModal({
      title: i18n.t("BoardServiceClient.ClientQuickCreate"),
      form: "formClient",
      wideMode: true,
      hideSaveButton: true,
      hideSaveAndCloseButton: false,
      content: <ClientQuickForm key={formKeys.formClient} ref={clientQuickFormRef} />,
      canClose: async () => {
        const isDirty = clientQuickFormRef.current?.isDirty?.();
        if (!isDirty) return true;

        return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
      },
      onSave: async () => {
        try {
          const isValid = clientQuickFormRef.current.validate();
          if (!isValid) return;

          const id = await ClientService.QuickCreate(clientQuickFormRef.current.getFormData());
          if (!id) return;
          await refreshClientList(id);
          closeModal();
        } catch (ex) {
          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          return false;
        }
      },
      onClose: (formName) => {
        handleCloseForm(formName);
      },
    });
  };

  const renderTabs = () => (
    <div className={styles.tabs}>
      {Object.values(Tabs).map((tab) => {
        const isDisabled = isLocked && tab !== Tabs.INFO;
        return (
          <div
            key={tab}
            style={{
              transition:
                "background-color 0.2s ease, " +
                "border-bottom 0.2s ease,  border-top 0.2s ease, border-left 0.2s ease,  border-right 0.2s ease, " +
                "font-weight 0.2s ease, padding 0.2s ease",
            }}
            className={
              activeTab === tab ? styles.activeTab : isDisabled ? styles.tabDisabled : styles.tab
            }
            onClick={() => !isDisabled && setActiveTab(tab)}>
            {tab}
          </div>
        );
      })}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case Tabs.INFO:
        return (
          selectedClient && (
            <ClientInfoTab
              client={selectedClientDetail}
              reloadDetailClient={(client) => handleSelectClient(client)}
              refreshClientList={refreshClientList}
              lockParent={(v) => {
                setIsLocked(v);
              }}
            />
          )
        );
      case Tabs.HORSES:
        return (
          selectedClient && (
            <ClientHorsesTab
              client={selectedClientDetail}
              reloadDetailClient={(client) => handleSelectClient(client, true)}
            />
          )
        );
      case Tabs.NOTES:
        return (
          selectedClient && (
            <ClientNotesTab
              client={selectedClientDetail}
              reloadDetailClient={(client, refresh) => handleSelectClient(client, refresh)}
            />
          )
        );
      case Tabs.SERVICES:
        return (
          selectedClient && (
            <ClientServicesTab
              client={selectedClientDetail}
              reloadDetailClient={(client, refresh) => handleSelectClient(client, refresh)}
            />
          )
        );
      case Tabs.MEDIA:
        return (
          selectedClient && (
            <ClientMediasTab
              client={selectedClientDetail}
              reloadDetailClient={(client, refresh) => handleSelectClient(client, refresh)}
            />
          )
        );
      default:
        return <div></div>;
    }
  };

  const filteredClients = getFilteredAndSortedClients();

  const renderClientDetail = () => {
    if (!selectedClient && isLoadingClientDetail) {
      return (
        <div className={styles.tabContent}>
          <div className={styles.loadingIndicator}>
            <LoadingSpinner />
          </div>
        </div>
      );
    }
    if (!selectedClient && !isLoadingClientDetail) {
      return <div className={styles.centerMessage}>{i18n.t("ClientPage.PleaseSelect")}</div>;
    }
    if (isLoadingClientDetail) {
      return (
        <div className={styles.tabContent}>
          <div className={styles.loadingIndicator}>
            <LoadingSpinner />
          </div>
        </div>
      );
    }

    const blockInteraction = isArchived && activeTab !== Tabs.INFO;

    return (
      <div
        className={`${styles.tabContent} ${blockInteraction ? styles.archivedReadOnly : ""}`}
        inert={blockInteraction}>
        {renderTabContent()}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div
        className={`${styles.clientsListSection} ${isLocked ? styles.clientsListSectionDisabled : ""}`}>
        <ListCardContainer
          headerLabel={i18n.t("SideBar.MenuClients")}
          addButtonAction={handleAddClientClick}
          isLoading={isFetchingClients}
          dataItems={filteredClients ?? []}
          onSelect={handleSelectClient}
          selectedItemExternalId={selectedClient?.externalId}
          type="client"
          ref={clientListRef}
          refresh={() => {
            refreshClientList();
          }}
          storageId={"ClientPage.Client"}
        />
      </div>
      <div className={`${styles.clientInfoSection} ${!selectedClient ? styles.faded : ""}`}>
        <div
          className={`${styles.boardSectionHeader} ${
            isArchived ? styles.boardSectionHeaderArchived : ""
          }`}>
          <div className={styles.headerContainer}>
            {selectedClient ? (
              <>
                <span className={styles.headerTitle} title={selectedClient?.cardName ?? ""}>
                  {selectedClient?.cardName ?? ""}
                </span>

                {isArchived && (
                  <span className={styles.archivedBadge}>{i18n.t("ClientPage.Archived")}</span>
                )}
              </>
            ) : (
              <span>...</span>
            )}
          </div>
          {isArchived && (
            <div className={styles.archivedNotice}>{i18n.t("ClientPage.ArchivedReadOnly")}</div>
          )}
        </div>
        {renderTabs()}
        {renderClientDetail()}
      </div>
    </div>
  );
};

export default Clients;
